/** @format */

import { axiosInstance } from '@/common/application/context/Providers';
import { DocumentTemplate } from '@common/application/enums/DocumentTemplate';
import { Resource } from '@common/application/enums/Resource';
import { ResourceUrl } from '@common/application/helpers/generateSignedUrlsForResults';
import uploadFileToSignedUrl from '@common/application/helpers/uploadFileToSignedUrl';
import { IDocumentImage, TDocumentStyle } from '@common/application/hooks/useDocument';
import { SavingRaw } from '@common/application/model/Saving';
import { Account } from '@common/model/apiClient/Account';
import { Branch } from '@common/model/apiClient/Branch';
import { CompanyBrand } from '@common/model/apiClient/CompanyBrand';
import { Contact } from '@common/model/apiClient/Contact';
import { DocumentSchedule } from '@common/model/apiClient/DocumentSchedule';
import { DocumentClient } from '@common/model/apiClient/index';
import { Site } from '@common/model/apiClient/Site';
import { Tag } from '@common/model/apiClient/Tag';
import { AxiosApiResponse } from '@common/types/api';
import { ElasticQueryOpts, ElasticResponse, ElasticSort } from '@common/types/elastic';
import { Nullable } from '@common/types/utils';
import { CompanyDocumentStyle } from '@prisma/client';
import { isPlainObject } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

export interface DocumentTag {
  documentId: number;
  companyId: number;
  tag: Tag;
  tagId: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
}

export class Document {
  id?: number;
  description: string;
  title: string;
  results: string;
  notes: string;
  branch: Branch;
  site: Site;
  contact: Contact;
  author: {
    id: string;
    givenName: string;
    familyName: string;
    email: string;
    jobTitle?: string;
    phone?: string;
  };
  createdBy: {
    id: string;
    givenName: string;
    familyName: string;
    email: string;
  };
  chart: boolean;
  percent: boolean;
  calculations: boolean;
  cover: boolean;
  logo: boolean;
  brands: Array<{ brand: CompanyBrand }>;
  savings: Array<SavingRaw>;
  documentSchedules: Array<DocumentSchedule>;
  companyId: number;
  company: { logo: ResourceUrl; companyDocumentStyle: CompanyDocumentStyle };
  account: Account;
  images: Array<IDocumentImage>;
  templateVersion: DocumentTemplate;
  showImages: boolean;
  styles: Record<DocumentTemplate, TDocumentStyle>;
  bannerId?: number;
  accountId: number;
  savingsTotal?: string;
  tags?: Array<Tag>;
  deletedAt: Date | null;

  static list(query, sort, queryOpts: ElasticQueryOpts, size?: number, pit?: Nullable<string>, search_after?: ElasticSort | null): ElasticResponse<DocumentClient> {
    // Ensure this code is not ran by SSR!
    if (typeof window !== 'undefined') {
      return axiosInstance
        .get(`/api/v1/documents`, {
          params: {
            sequence_id: 1,
            pit,
            query,
            queryOpts: JSON.stringify(queryOpts),
            sort: JSON.stringify(sort),
            search_after: JSON.stringify(search_after),
            size,
          },
        })
        .then((res) => ({ ...res.data }));
    }
    throw new Error();
  }

  static getByTags(tags: Array<number>): Promise<{ message: string; paginated: boolean; payload: Array<Document> }> {
    // Ensure this code is not ran by SSR!
    if (typeof window !== 'undefined') {
      return axiosInstance
        .get(`/api/v1/documents/tags`, {
          params: {
            sequence_id: 1,
            tags: JSON.stringify(tags),
          },
        })
        .then((res) => ({ ...res.data }));
    }
    throw new Error();
  }

  static autocomplete(query, config) {
    if (typeof window !== 'undefined') {
      return axiosInstance
        .get(`/api/v1/documents/search`, {
          signal: config.signal,
          params: {
            sequence_id: 1,
            query,
          },
        })
        .then((res) => ({ ...res.data }));
    }
    throw new Error();
  }

  static get(id: number): AxiosApiResponse<Document> {
    return axiosInstance.get(`/api/v1/documents/${id}`);
  }

  static processImagesForSignedUrlUpload(payload) {
    const fileData: Array<{ id: string; file: string }> = [];
    // Recursive function to find any images and prepare them to be uploaded to bucket and linked to resource using a signedUrl
    function processObject(obj) {
      if (isPlainObject(obj)) {
        const newObj = {};

        for (const key in obj) {
          if (Array.isArray(obj[key])) {
            newObj[key] = obj[key].map((item) => {
              if (isPlainObject(item)) {
                return processObject(item);
              } else {
                return item;
              }
            });
            //   We only make changes if we find an images key which also has a "create" property - i.e. only for new images
          } else if (key === 'images' && isPlainObject(obj[key]) && obj[key].hasOwnProperty('create') && obj[key].create) {
            newObj[key] = {
              ...obj[key],
              create: obj[key]?.create?.map((image) => {
                const id = uuidv4();
                fileData.push({ id: id, file: image.url });
                const updatedImage = {
                  ...image,
                  id: id,
                  fileExt: 'jpeg', // Change this to your desired value
                };
                delete updatedImage.url;
                return updatedImage;
              }),
            };
          } else if (isPlainObject(obj[key])) {
            newObj[key] = processObject(obj[key]);
          } else {
            newObj[key] = obj[key];
          }
        }

        return newObj;
      } else {
        return obj;
      }
    }

    const modifiedPayload = processObject(payload);
    return { payload: modifiedPayload, fileData };
  }

  static save(documentId, payload) {
    if (!payload) return Promise.reject(new Error('Invalid payload'));
    if (!documentId) {
      return Promise.reject(new Error('No document ID'));
    }
    const { payload: mappedPayload, fileData } = DocumentClient.processImagesForSignedUrlUpload(payload);
    return axiosInstance.patch(`/api/v1/documents/${documentId}`, { payload: mappedPayload }).then(async (res) => {
      const { signedUrls } = res.data.payload;
      if (signedUrls) {
        for await (const url of signedUrls) {
          const file = fileData.find((f) => f.id === url.id);
          await uploadFileToSignedUrl(url.url, file?.file ?? null, {}, 'image', Resource.DOCUMENT_IMAGE);
        }
      }
      return res.data;
    });
  }

  static async saveNew(payload) {
    if (!payload) return Promise.reject(new Error('Invalid payload'));
    const { payload: mappedPayload, fileData } = DocumentClient.processImagesForSignedUrlUpload(payload);
    return axiosInstance.post(`/api/v1/documents`, { payload: mappedPayload }).then(async (res) => {
      const { signedUrls } = res.data.payload;
      if (signedUrls) {
        for await (const url of signedUrls) {
          const file = fileData.find((f) => f.id === url.id);
          await uploadFileToSignedUrl(url.url, file?.file ?? null, {}, 'image', Resource.DOCUMENT_IMAGE);
        }
      }
      return res.data;
    });
  }

  static delete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.delete(`/api/v1/documents/${id}`).then((res) => ({ ...res.data }));
  }

  static undelete(id: number): Promise<Record<string, unknown>> {
    return axiosInstance.post(`/api/v1/documents/${id}/undelete`).then((res) => ({ ...res.data }));
  }
}
